import { gql } from 'graphql-tag';

// =============================================================================
// Team — GraphQL operations against the Nexus backend (Program-Modules epic, W6)
// =============================================================================
// The FINAL retirement: the team ACTIVITY becomes a program MODULE. The
// `ProgramTeam` collection + `isTeamAdk` write-routing STAY (load-bearing); only
// the activity is retired. team.service.ts (create/join/switch/archive) is
// UNCHANGED — the existing formation UI (ParticipantTeamDialog → m-team →
// m-create-team / m-join-team) is the module's panel content, untouched.
//
// This file mirrors coaching.ts / places.ts: ONE runner-consumable read op behind
// GraphQLJSON (no sub-selection), plus the client-side shapes.
//
// The distinctive shape here is the GATE. Team is "required but flexible": a
// participant must be on a team at some point BEFORE the first `isTeamAdk:true`
// activity in the chain. The server computes that gate at read time (no
// denormalization) and returns it. `gate.blocked === true` = the participant has
// reached (or is about to reach) the first team activity WITHOUT a team → the
// module owes a "join or create a team" action.
// =============================================================================

// ------- Queries -------

/**
 * teamModuleState(programId) — the Program-Modules framework view of team
 * (Program-Modules epic, W6/W7). Returns the GraphQLJSON scalar (NO sub-selection),
 * mirroring coachingModuleState / placesModuleState. Distinct from the legacy
 * team-activity completion check (`teamDoc.members.includes(participantId)`): this
 * folds the "required-before-first-isTeamAdk" GATE into one runner-consumable
 * payload. `deriveState` is a pure pass-through of the gate (blocked → 'due').
 */
export const TEAM_MODULE_STATE_QUERY = gql`
  query TeamModuleState($programId: String!) {
    teamModuleState(programId: $programId)
  }
`;

// ------- Mutations -------

/**
 * setProgramModule(programId, key, patch) — merges one module config entry into
 * AutoPilot.modules (the canonical program-settings writer; see
 * program-settings.resolver.ts). Returns the GraphQLJSON scalar (NO sub-selection).
 *
 * Team Onboarding uses this to flip the team gate's `promptTiming` (key='team',
 * patch={ promptTiming: 'onboarding' | 'at-activity' }) — the creator's opt-in for
 * surfacing team formation during onboarding. Dark behind PROGRAM_MODULES + TEAM_ONBOARDING
 * at the call site; the resolver itself is flag-gated by PROGRAM_MODULES.
 */
export const SET_PROGRAM_MODULE_MUTATION = gql`
  mutation SetProgramModule($programId: String!, $key: String!, $patch: JSON!) {
    setProgramModule(programId: $programId, key: $key, patch: $patch)
  }
`;

// ------- Client-side shapes (mirror the backend DTOs) -------

/**
 * The resolved team gate. Server-computed at read time from the program's activity
 * chain (`Program.adks[].isTeamAdk`) + the participant's team membership
 * (`ProgramTeam.members`):
 *   - enabled            — the team module is on for this program
 *   - required           — a team is required (the flexible-required intent; a clean
 *                          flip to solo-first later = required:false)
 *   - onTeam             — the participant is currently on a team for this program
 *   - firstTeamAdkIndex  — index of the first `isTeamAdk:true` activity (null = none)
 *   - firstTeamAdkName   — that activity's display name (for the nudge copy)
 *   - reached            — the participant has reached that first team activity
 *   - blocked            — required && !onTeam && the first team activity is imminent/
 *                          reached → the participant must join/create a team to continue
 */
export interface TeamGate {
  enabled: boolean;
  required: boolean;
  onTeam: boolean;
  firstTeamAdkIndex: number | null;
  firstTeamAdkName: string | null;
  reached: boolean;
  blocked: boolean;
  // ---- Capstone hard-gate envelope (BE parallel change; all OPTIONAL so older
  // payloads keep working — the FE reads them defensively) ----
  /**
   * Gate enforcement mode. Default (absent / 'hard') = HARD route block: the
   * participant cannot advance PAST the first isTeamAdk activity until on a team.
   * 'soft' = fall back to the legacy dismissible nudge (no advance block).
   */
  mode?: 'hard' | 'soft';
  /**
   * Escape hatch (future solo-first flag). When true the participant is NOT
   * hard-blocked even if required && blocked — they may proceed solo.
   */
  allowSolo?: boolean;
  /** Team-scoped activity indices (BE parallel change); carried through defensively. */
  sharedActivities?: string[];
  /**
   * WHEN the runner AUTO-OPENS the join/create panel (transparent config surface;
   * see TEAM_MODULE_DEFAULTS.promptTiming). OPTIONAL so older payloads keep working —
   * the FE defaults to 'at-activity' when absent.
   *   - 'at-activity' (default): auto-open only once the participant is AT/after the
   *     first isTeamAdk activity (currentPage >= firstTeamAdkIndex) — no persistent
   *     early pop before they reach the gate.
   *   - 'onboarding': the legacy posture — auto-open persistently from page 0.
   * This ONLY moves the auto-open timing; the HARD advancement gate (mode/blocked,
   * enforced in ParticipantGuide.teamHardBlockAt) is unaffected.
   */
  promptTiming?: 'at-activity' | 'onboarding';
}

/**
 * teamModuleState payload. Mirrors CoachingModuleState / PlacesModuleState but
 * carries a GATE instead of a flat `state`/`policy` — the FE folds gate.blocked into
 * the framework `state` ('due' when blocked, else 'idle') in useTeamModule.
 */
export interface TeamModuleState {
  enabled: boolean;
  gate: TeamGate;
}

/** Server-derived nudge state. Mirrors the ModuleNudgeState union. */
export type TeamModuleNudgeState = 'idle' | 'due' | 'overdue';
